
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import memoizeOne from 'memoize-one';
import type { FieldInput, FieldMeta } from '@pinto/react-form';

import type { HTMLDivProps } from 'src/typeDefs';
import addPassiveDOMListener from 'src/utils/addPassiveListener';

/**
 * A component used to render every row in a JSONRenderer
 */
export type JSONRowRenderer = React.ComponentType<
    {
        data: JsonRow;
        /** Total number of rows */
        total: number;
        /** Render line numbers at the start of every row. Useful for debugging */
        lineNumbers: boolean;
    }
    & HTMLDivProps
>;

export interface JSONRendererProps extends Omit<HTMLDivProps, 'onError'> {
    data: any;
    /**
     * When enabled, the input data will be passed through `JSON.parse(JSON.stringify(data))` to
     * ensure it's proper JSON.
     * This has some consequences like removing undefined-s from the output, throwing an exception if the JSON is cyclical.
     * It also has a performance impact, especially with large inputs
     */
    cleanData?: boolean;
    /**
     * Sort object keys when rendering
     */
    sortKeys?: boolean;
    /**
     * Passing this will cause the JSONRenderer to become editable
     */
    onChange?: (data: any) => void;
    /**
     * If `onChange` is enabled, this handler will be called whenever the inputted JSON is not valid
     */
    onError?: (error: Error) => void;
    /**
     * You can use a custom renderer to add extra features.
     * E.g. JSONCoverage overrides the renderer to add coverage highlighting
     */
    RowRenderer?: JSONRowRenderer;
    /**
     * Only render rows that are visible in the viewport and update it as it scrolls
     *
     * *NOTE:* This should work paired with edit mode (via `onChange`), lazy rendering is disabled during focus
     */
    lazyRender?: boolean;
    /**
     * A positive integer depth value after which to stop showing rows
     * Useful for getting an overview of the data in large JSONs
     */
    collapseDepth?: number;
    /**
     * Render line numbers at the start of every row
     * Useful for debugging
     */
    lineNumbers?: boolean;
};

export interface JSONRendererFieldProps extends Omit<JSONRendererProps, 'data'> {
    input: FieldInput;
    meta?: FieldMeta;
};

export type JsonRow = {
    /**
     * An unique string that is used as the react component key prop
     */
    id: string;
    index: number;
    path: (string | number)[];
    /**
     * All the primitive types (from `typeof item`) and 2 custom types: null and syntax
     */
    type: 'null' | 'syntax' | 'string' | 'boolean' | 'number' | 'object';
    value: any;
    depth: number;
    key?: string;
    comma?: boolean;
    indent?: number;
};

export class DefaultRowRenderer extends React.Component<React.ComponentProps<JSONRowRenderer>> {
    render () {
        const {
            data: { type, value, key, comma, depth },
            total,
            lineNumbers,
            ...rest
        } = this.props;
        let prefix;

        if (key) prefix = <ObjectKey>{`"${key}": `}</ObjectKey>;

        return <Item {...rest}>
            {lineNumbers &&
                String(this.props.data.index).padStart(Math.ceil(Math.log10(total))) + ' | '
            }
            {depth > 0 && '    '.repeat(depth)}
            {prefix}
            <span data-type={type}>{type === 'string' ? `"${value}"` : String(value)}</span>
            {comma && ','}
        </Item>;
    }
}

/**
 * Renders a plain JS object as JSON using react components
 * The given object is flattened and rendered row-by-row.
 *  This allows for some optimizations like not rendering rows that are outside of the viewport
 */
export class JSONRenderer extends React.PureComponent<JSONRendererProps> {
    static propTypes = {
        data: PropTypes.any,
        cleanData: PropTypes.bool,
        sortKeys: PropTypes.bool,
        RowRenderer: PropTypes.elementType.isRequired,
    };

    static defaultProps = {
        cleanData: false,
        sortKeys: false,
        RowRenderer: DefaultRowRenderer,
    };

    static getDerivedStateFromProps(nextProps: JSONRendererProps, state: any) {
        if (state._data === nextProps.data && state._lazyRender === nextProps.lazyRender) return null;

        return {
            // change the root key every time data changes to force re-render and prevent weird edit behaviors with keys potentially not changing
            key: Math.random(),
            // data or lazyRender change should re-initialze
            initialized: false,
            // cache props in state to compare next time since there's no more componentWillReceiveProps
            _data: nextProps.data,
            _lazyRender: nextProps.lazyRender,
        };
    }

    state = {
        key: null,
        focused: false,
        // -- lazyRender state --
        initialized: false,
        lineHeight: 0,
        startIndex: 0,
        endIndex: 0,
    }

    _removeScrollListener?: ReturnType<typeof addPassiveDOMListener>;
    _rootElem?: HTMLDivElement;

    componentWillUnmount(){
        this._removeScrollListener?.();
    }

    baseGetData = memoizeOne((data, cleanData, sortKeys, collapseDepth) => {
        if (cleanData) data = JSON.parse(JSON.stringify(data));

        let flatData = flattenJson(data, { sortKeys });
        if (collapseDepth) flatData = flatData.filter(row => row.depth <= collapseDepth);

        return {
            flatData,
            maxDepth: flatData.reduce((max, row) => row.depth > max ? row.depth : max, 0),
        };
    });

    getData() {
        const { data, cleanData, onChange, sortKeys, collapseDepth } = this.props;
        const { focused } = this.state;
        return this.baseGetData(
            data,
            cleanData || !!onChange,
            sortKeys,
            // disable collapsedDepth when focused (in edit mode) to prevent invalid JSON
            focused ? undefined : collapseDepth,
        );
    }

    setVisibleRange = (startIndex: number, endIndex: number) => {
        startIndex = Math.round(startIndex);
        endIndex = Math.round(endIndex);

        if (startIndex !== this.state.startIndex || endIndex !== this.state.endIndex) {
            this.setState({ startIndex, endIndex });
        }
    }

    updateVisible = () => {
        if (!this._rootElem) return this.setVisibleRange(0, 0);

        const { lineHeight } = this.state;
        const { flatData } = this.getData();
        const windowInnerHeight = window.innerHeight;
        let toRenderCount = Math.round(windowInnerHeight / lineHeight);
        const { top, bottom } = this._rootElem.getBoundingClientRect();
        const bufferCount = Math.round(toRenderCount * 0.5); // how many extra to render before/after

        // do not render anything when out of bounds
        if (top > windowInnerHeight || bottom < 0) return this.setVisibleRange(0, 0);

        let startIndex: number, endIndex: number;

        if (top > 0) {
            startIndex = 0;
            // toRenderCount -= top / lineHeight;
        } else startIndex = top * -1 / lineHeight;

        startIndex = _.clamp(startIndex - bufferCount, 0, flatData.length);
        endIndex = _.clamp(startIndex + toRenderCount + bufferCount * 2, startIndex, flatData.length);

        this.setVisibleRange(startIndex, endIndex);
    }

    updateVisibleThrottled = _.throttle(this.updateVisible, 16);
    updateVisibleOnInit = _.once(this.updateVisible);

    setRootRef = (elem: HTMLDivElement) => {
        if (this._rootElem && (!elem || elem === this._rootElem)) return;
        this._rootElem = elem;
        this.updateVisibleOnInit();
        this._removeScrollListener?.();
        const listeners = getScrollParents(this._rootElem).map(parent =>
            addPassiveDOMListener(
                parent,
                'scroll',
                this.updateVisibleThrottled
            )
        );
        this._removeScrollListener = () => {
            listeners.forEach(unbind => unbind());
        };
    }

    render() {
        const {
            data,
            cleanData,
            sortKeys,
            onChange,
            RowRenderer,
            onError,
            lazyRender,
            collapseDepth,
            lineNumbers,
            ...rest
        } = this.props as Required<JSONRendererProps>;
        const { key, initialized, lineHeight, startIndex, endIndex, focused } = this.state;
        const rootProps = {
            ...rest,
            key,
        };
        const { flatData } = this.getData();

        if (onChange) {
            Object.assign(rootProps, {
                contentEditable: 'plaintext-only',
                suppressContentEditableWarning: true,
                spellCheck: false,
                onFocus: () => this.setState({ focused: true }),
                onBlur: ({ target: { innerText } }: React.ChangeEvent<HTMLDivElement>) => {
                    this.setState({ focused: false })
                    try {
                        const newValue = JSON.parse(innerText);
                        onChange(newValue);
                    } catch (e: any) {
                        if (onError) onError(e);
                    }
                },
            });
        }

        const renderContent = (rowList: JsonRow[]) =>
            <Root {...rootProps}>
                {rowList.map(row =>
                    <RowRenderer
                        key={row.id}
                        data={row}
                        total={flatData.length}
                        lineNumbers={lineNumbers}
                    />
                )}
            </Root>;

        // No point optimizing for small datasets
        if (!lazyRender || focused || flatData.length < 100) {
            return renderContent(flatData);
        }

        if (!initialized) {
            return <div
                {...rest}
                style={{ ...rest.style, padding: 0, visibility: 'hidden' }}
                children='Test'
                ref={elem => {
                    if (!elem) return;
                    this.setState({ initialized: true, lineHeight: elem.offsetHeight });
                }}
            />
        }

        Object.assign(rootProps, {
            style: {
                ...rest.style,
                height: 'unset',
                minHeight: this.state.lineHeight * flatData.length,
                paddingTop: lineHeight * startIndex,
            },
            ref: this.setRootRef,
        });

        return renderContent(
            initialized
                ? flatData.slice(startIndex, endIndex)
                : []
        );
    }
}

export default JSONRenderer;

export const JSONRendererField: React.FC<JSONRendererFieldProps> = ({ input, meta, ...rest }) =>
    <JSONRenderer {...rest} data={input.value} />;

function getScrollParents(node: HTMLElement) {
    const result: (HTMLElement | Window)[] = [];

    let current: HTMLElement | null = node;
    while (current) {
        // using the bellow condition is not robust enough
        // it fails to pick up scroll parents when the children is smaller than the parent height
        // for our purposes we want all parents that have a potential scroll and just getting all parents
        // is an easy way to achieve it
        // if (current.scrollHeight > current.clientHeight) {
        result.push(current);
        current = current.parentElement;
    }

    if (!result.includes(window)) result.unshift(window);

    return result;
}

const SLASH_REG = /\\/g;
const NEWLINE_REG = /\n/g;
const QUOTE_REG = /"/g;

/**
 * Converts a deeply nested JSON object into a flat list of JsonRow types
 * This is the main datatype used by the renderers
 */
const flattenJson = (obj: any, { sortKeys } = { sortKeys: false }) => {
    const result: JsonRow[] = [];

    const iterate = (
        item: any,
        path: JsonRow['path'],
        key?: JsonRow['key'],
        comma?: JsonRow['comma'],
    ) => {
        let itemType: JsonRow['type'] = 'null';
        let rowCount = 0;
        const add = (type: JsonRow['type'], value: JsonRow['value']) => {
            // escape common parsing corner-cases
            if (type === 'string') {
                value = value.replace(SLASH_REG, '\\\\');
                value = value.replace(NEWLINE_REG, '\\n');
                value = value.replace(QUOTE_REG, '\\"');
            }

            const row: JsonRow = {
                id: path.join('.') + '_' + rowCount,
                index: result.length,
                path,
                type,
                value,
                depth: path.length,
            };

            if (rowCount === 0) row.key = key;

            // all cases for when to add a comma (if it exists) at the end of a row
            if (rowCount > 0 || type !== 'syntax' || (itemType === 'object' && _.isEmpty(item))) {
                row.comma = comma;
            }

            result.push(row);
            ++rowCount;
        }

        if (item === null) return add('null', 'null')
        if (item === undefined) return add('null', 'undefined');

        if (Array.isArray(item)) {
            itemType = 'object';
            if (!item.length) return add('syntax', '[]');
            add('syntax', '[');
            item.forEach((child, index) => {
                iterate(child, [...path, index], undefined, index < item.length - 1);
            });
            add('syntax', ']');
            return
        }

        itemType = typeof item as JsonRow['type'];

        if (itemType === 'object') {
            if (_.isEmpty(item)) return add('syntax', '{}');
            add('syntax', '{');
            const pairs = Object.entries(item);
            if (sortKeys) pairs.sort((a, b) => a[0].localeCompare(b[0]));
            for (let [key, value] of pairs) {
                iterate(value, [...path, key], key, key !== _.last(pairs)?.[0]);
            }
            add('syntax', '}');
            return
        }

        add(itemType, item);
    }

    iterate(obj, []);

    return result;
}


require('!style-loader/lib/addStyles.js')([[ module.id, ".flex {\n  display: flex;\n}\n.flex.flex-space-between {\n  align-items: space-between;\n}\n.center {\n  text-align: center;\n}\n.bold {\n  font-weight: bold;\n}\n.mt0 {\n  margin-top: 0rem !important;\n}\n.mb0 {\n  margin-bottom: 0rem !important;\n}\n.ml0 {\n  margin-left: 0rem !important;\n}\n.mr0 {\n  margin-right: 0rem !important;\n}\n.m0 {\n  margin: 0rem 0rem 0rem 0rem !important;\n}\n.mx0 {\n  margin-left: 0rem !important;\n  margin-right: 0rem !important;\n}\n.my0 {\n  margin-top: 0rem !important;\n  margin-bottom: 0rem !important;\n}\n.mt1 {\n  margin-top: 1rem !important;\n}\n.mb1 {\n  margin-bottom: 1rem !important;\n}\n.ml1 {\n  margin-left: 1rem !important;\n}\n.mr1 {\n  margin-right: 1rem !important;\n}\n.m1 {\n  margin: 1rem 1rem 1rem 1rem !important;\n}\n.mx1 {\n  margin-left: 1rem !important;\n  margin-right: 1rem !important;\n}\n.my1 {\n  margin-top: 1rem !important;\n  margin-bottom: 1rem !important;\n}\n.mt2 {\n  margin-top: 2rem !important;\n}\n.mb2 {\n  margin-bottom: 2rem !important;\n}\n.ml2 {\n  margin-left: 2rem !important;\n}\n.mr2 {\n  margin-right: 2rem !important;\n}\n.m2 {\n  margin: 2rem 2rem 2rem 2rem !important;\n}\n.mx2 {\n  margin-left: 2rem !important;\n  margin-right: 2rem !important;\n}\n.my2 {\n  margin-top: 2rem !important;\n  margin-bottom: 2rem !important;\n}\n.mt05 {\n  margin-top: 0.5rem !important;\n}\n.mb05 {\n  margin-bottom: 0.5rem !important;\n}\n.ml05 {\n  margin-left: 0.5rem !important;\n}\n.mr05 {\n  margin-right: 0.5rem !important;\n}\n.m05 {\n  margin: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.mx05 {\n  margin-left: 0.5rem !important;\n  margin-right: 0.5rem !important;\n}\n.my05 {\n  margin-top: 0.5rem !important;\n  margin-bottom: 0.5rem !important;\n}\n.mt025 {\n  margin-top: 0.25rem !important;\n}\n.mb025 {\n  margin-bottom: 0.25rem !important;\n}\n.ml025 {\n  margin-left: 0.25rem !important;\n}\n.mr025 {\n  margin-right: 0.25rem !important;\n}\n.m025 {\n  margin: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.mx025 {\n  margin-left: 0.25rem !important;\n  margin-right: 0.25rem !important;\n}\n.my025 {\n  margin-top: 0.25rem !important;\n  margin-bottom: 0.25rem !important;\n}\n.pt0 {\n  padding-top: 0rem !important;\n}\n.pb0 {\n  padding-bottom: 0rem !important;\n}\n.pl0 {\n  padding-left: 0rem !important;\n}\n.pr0 {\n  padding-right: 0rem !important;\n}\n.p0 {\n  padding: 0rem 0rem 0rem 0rem !important;\n}\n.px0 {\n  padding-left: 0rem !important;\n  padding-right: 0rem !important;\n}\n.py0 {\n  padding-top: 0rem !important;\n  padding-bottom: 0rem !important;\n}\n.pt1 {\n  padding-top: 1rem !important;\n}\n.pb1 {\n  padding-bottom: 1rem !important;\n}\n.pl1 {\n  padding-left: 1rem !important;\n}\n.pr1 {\n  padding-right: 1rem !important;\n}\n.p1 {\n  padding: 1rem 1rem 1rem 1rem !important;\n}\n.px1 {\n  padding-left: 1rem !important;\n  padding-right: 1rem !important;\n}\n.py1 {\n  padding-top: 1rem !important;\n  padding-bottom: 1rem !important;\n}\n.pt2 {\n  padding-top: 2rem !important;\n}\n.pb2 {\n  padding-bottom: 2rem !important;\n}\n.pl2 {\n  padding-left: 2rem !important;\n}\n.pr2 {\n  padding-right: 2rem !important;\n}\n.p2 {\n  padding: 2rem 2rem 2rem 2rem !important;\n}\n.px2 {\n  padding-left: 2rem !important;\n  padding-right: 2rem !important;\n}\n.py2 {\n  padding-top: 2rem !important;\n  padding-bottom: 2rem !important;\n}\n.pt05 {\n  padding-top: 0.5rem !important;\n}\n.pb05 {\n  padding-bottom: 0.5rem !important;\n}\n.pl05 {\n  padding-left: 0.5rem !important;\n}\n.pr05 {\n  padding-right: 0.5rem !important;\n}\n.p05 {\n  padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.px05 {\n  padding-left: 0.5rem !important;\n  padding-right: 0.5rem !important;\n}\n.py05 {\n  padding-top: 0.5rem !important;\n  padding-bottom: 0.5rem !important;\n}\n.pt025 {\n  padding-top: 0.25rem !important;\n}\n.pb025 {\n  padding-bottom: 0.25rem !important;\n}\n.pl025 {\n  padding-left: 0.25rem !important;\n}\n.pr025 {\n  padding-right: 0.25rem !important;\n}\n.p025 {\n  padding: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.px025 {\n  padding-left: 0.25rem !important;\n  padding-right: 0.25rem !important;\n}\n.py025 {\n  padding-top: 0.25rem !important;\n  padding-bottom: 0.25rem !important;\n}\n.font-small-1 {\n  font-size: 0.857142857142857rem;\n}\n.font-large-1 {\n  font-size: 1.142857142857143rem;\n}\n.font-small-2 {\n  font-size: 0.714285714285714rem;\n}\n.font-large-2 {\n  font-size: 1.285714285714286rem;\n}\n.font-small-3 {\n  font-size: 0.571428571428571rem;\n}\n.font-large-3 {\n  font-size: 1.428571428571429rem;\n}\n.font-small-4 {\n  font-size: 0.428571428571429rem;\n}\n.font-large-4 {\n  font-size: 1.571428571428571rem;\n}\n.JSONRenderer-Root--3xOED {\n  white-space: pre;\n  font-family: monospace, courier;\n  color: #1f4b99;\n}\n.JSONRenderer-Root--3xOED [data-type='string'] {\n  color: #008075;\n}\n.JSONRenderer-Root--3xOED [data-type='number'] {\n  color: #2965cc;\n}\n.JSONRenderer-Root--3xOED [data-type='boolean'] {\n  color: #106ba3;\n}\n.JSONRenderer-Root--3xOED [data-type='null'] {\n  color: #a66321;\n}\n.JSONRenderer-Item--3O8Re {\n  display: block;\n}\n.JSONRenderer-ObjectKey--3Z1KZ {\n  display: inline-block;\n}\n.bp3-dark .JSONRenderer-ObjectKey--3Z1KZ {\n  color: #48aff0;\n}\n.bp3-dark .JSONRenderer-Root--3xOED {\n  color: #48aff0;\n}\n.bp3-dark .JSONRenderer-Root--3xOED [data-type='string'] {\n  color: #14ccbd;\n}\n.bp3-dark .JSONRenderer-Root--3xOED [data-type='number'] {\n  color: #669eff;\n}\n.bp3-dark .JSONRenderer-Root--3xOED [data-type='boolean'] {\n  color: #669eff;\n}\n.bp3-dark .JSONRenderer-Root--3xOED [data-type='null'] {\n  color: #ffb366;\n}\n", '' ]]);
const { RSL_COMPONENT } = require("!../../node_modules/@pinto/react-style-loader/react-style-loader.runtime.compiled.js");
const __Root = RSL_COMPONENT('Root', 'JSONRendererRoot', 'JSONRenderer-Root--3xOED');
 const Root = __Root
const __Item = RSL_COMPONENT('Item', 'JSONRendererItem', 'JSONRenderer-Item--3O8Re');
 const Item = __Item
const __ObjectKey = RSL_COMPONENT('ObjectKey', 'JSONRendererObjectKey', 'JSONRenderer-ObjectKey--3Z1KZ');
 const ObjectKey = __ObjectKey

